// src/api/constants.ts
var LocationSource = /* @__PURE__ */ ((LocationSource2) => {
  LocationSource2["Cookies"] = "cookies";
  LocationSource2["Default"] = "default";
  LocationSource2["Ipaddress"] = "ipaddress";
  LocationSource2["Zip5"] = "zip5";
  return LocationSource2;
})(LocationSource || {});
var CookieNames = /* @__PURE__ */ ((CookieNames2) => {
  CookieNames2["City"] = "CVCurrentCity";
  CookieNames2["CVAccessToken"] = "CVAccessToken";
  CookieNames2["CVIdToken"] = "CVIdToken";
  CookieNames2["IpAddress"] = "CVCurrentIpAddress";
  CookieNames2["MerchHasLoggedIn"] = "MerchHasLoggedIn";
  CookieNames2["State"] = "CVCurrentState";
  CookieNames2["Zip"] = "CVCurrentZip";
  return CookieNames2;
})(CookieNames || {});
var DefaultLocation = {
  state: "GA",
  city: "Atlanta",
  zip: "30303",
  ipAddress: ""
};
var TwoWeeks = 1209600;

// src/api/geolocationApi.ts
import jwtDecode from "jwt-decode";

// src/api/lib/cFetch.ts
import fetch from "cross-fetch";
async function cFetch(url, options) {
  const res = await fetch(url, options);
  return res.json();
}

// src/api/lib/formatLocationData.ts
function formatLocationData(data) {
  const { city, zip5: zip } = data;
  const state = data.state ?? data.stateAbbreviation;
  if (!city || !state || !zip) {
    throw new Error("Missing required location data");
  }
  return { city, state, zip };
}

// src/api/lib/isValidZipCode.ts
function isValidZipCode(value) {
  return /^\d{5}$/.test(value);
}

// src/api/lib/formatLocationOnError.ts
function formatLocationOnError(cookies) {
  const {
    CVCurrentCity: city,
    CVCurrentState: state,
    CVCurrentZip: zip,
    CVCurrentIpAddress: ipAddress
  } = Object.fromEntries(cookies.getAll() ?? []);
  return city && state && isValidZipCode(zip) ? { city, ipAddress, state, zip } : DefaultLocation;
}

// src/api/lib/getLocationFromCookies.ts
var EXPECTED_COOKIE_COUNT = 5;
function getLocationFromCookies(cookies, ipAddress) {
  const location = {
    state: cookies.get("CVCurrentState" /* State */) ?? "",
    city: cookies.get("CVCurrentCity" /* City */) ?? "",
    zip: cookies.get("CVCurrentZip" /* Zip */) ?? "",
    ipAddress: cookies.get("CVCurrentIpAddress" /* IpAddress */) ?? ipAddress ?? "",
    source: "cookies" /* Cookies */
  };
  const receivedCookieCount = Object.values(location).filter(Boolean).length;
  return receivedCookieCount === EXPECTED_COOKIE_COUNT ? location : null;
}

// src/api/lib/isServerSide.ts
var isServerSide = function serverSideCheck() {
  try {
    return typeof window === "undefined";
  } catch (e) {
    return true;
  }
}();
var isServerSide_default = isServerSide;

// src/api/lib/LoginCookie.ts
var LoginCookie = class {
  constructor(cookieClient) {
    this.cookieClient = cookieClient;
  }
  path = "/";
  get hasAccessTokenCookie() {
    return Boolean(this.cookieClient.get("CVAccessToken" /* CVAccessToken */));
  }
  get hasLoginCookie() {
    return this.cookieClient.get("MerchHasLoggedIn" /* MerchHasLoggedIn */) === "true";
  }
  get shouldSetCookie() {
    return this.hasAccessTokenCookie && !this.hasLoginCookie;
  }
  get shouldRemoveCookie() {
    return this.hasLoginCookie && !this.hasAccessTokenCookie;
  }
  set() {
    this.cookieClient.set("MerchHasLoggedIn" /* MerchHasLoggedIn */, "true", {
      path: this.path
    });
  }
  remove() {
    this.cookieClient.set("MerchHasLoggedIn" /* MerchHasLoggedIn */, "", {
      expires: "Thu, 01 Jan 1970 00:00:00 UTC",
      path: this.path
    });
  }
};

// src/api/lib/capitalize.ts
function capitalize(str) {
  const firstLetter = str.charAt(0).toUpperCase();
  const restOfKey = str.slice(1);
  return `${firstLetter}${restOfKey}`;
}

// src/api/lib/setCookies.ts
function setCookies(client, cookies, options = {}) {
  const setCookie = ([key, value]) => {
    if (value)
      client.set(`CVCurrent${capitalize(key)}`, value, options);
  };
  Object.entries(cookies).forEach(setCookie);
}

// src/api/lib/toError.ts
function toError(err) {
  return err instanceof Error ? err : new Error(String(err));
}

// src/api/geolocationApi.ts
var GeoLocationApi = class {
  constructor(cookies, internalConfig, fetcher = cFetch, fetcherWithAuth = fetcher) {
    this.cookies = cookies;
    this.internalConfig = internalConfig;
    this.fetcher = fetcher;
    this.fetcherWithAuth = fetcherWithAuth;
    this.loginCookie = new LoginCookie(cookies);
  }
  loginCookie;
  async getGeoLocation() {
    const location = await this.maybeGetAccountLocation() ?? this.getLocationFromCookies() ?? await this.getGeoLocationFromIpAddress();
    this.setLocationCookies(location);
    return location;
  }
  async setGeoLocation(zip) {
    const location = await this.maybeGetAccountLocation() ?? await this.setGeoLocationWithZip(zip);
    this.setLocationCookies(location);
    return location;
  }
  async getAccountLocation() {
    try {
      const CVAccessToken = this.cookies.get("CVAccessToken" /* CVAccessToken */);
      const CVIdToken = this.cookies.get("CVIdToken" /* CVIdToken */);
      if (!CVAccessToken || !CVIdToken)
        return "";
      const userInfo = jwtDecode(CVIdToken);
      const userProfile = await this.fetcherWithAuth(
        `${this.internalConfig.contactsApi}/${userInfo.user_id}/addresses`
      );
      const zipCodes = userProfile?.content?.reduce(
        (zipcodes, address) => {
          const kind = address?.kind;
          const zip5 = address?.address?.zipCode?.zip5;
          return { ...zipcodes, [kind]: zip5 };
        },
        {}
      );
      return zipCodes?.DeliveryZip ?? zipCodes?.VerifiedDelivery ?? zipCodes?.VerifiedHome ?? "";
    } catch (e) {
      return "";
    }
  }
  // if the user logs in, and we haven't already set the MerchHasLoggedIn
  // cookie, then prioritize their account zip over the seo location
  async maybeGetAccountLocation() {
    if (this.loginCookie.shouldRemoveCookie) {
      this.loginCookie.remove();
      return void 0;
    }
    if (!this.loginCookie.shouldSetCookie) {
      return void 0;
    }
    const zip = await this.getAccountLocation();
    if (zip && !isServerSide_default) {
      this.loginCookie.set();
    }
    return zip ? this.setGeoLocationWithZip(zip) : void 0;
  }
  getLocationFromCookies() {
    const location = getLocationFromCookies(
      this.cookies,
      this.internalConfig.ipAddress
    );
    if (!isValidZipCode(location?.zip ?? "")) {
      return null;
    }
    if (location?.ipAddress) {
      this.cookies.set("CVCurrentIpAddress", location.ipAddress, { path: "/" });
    }
    return location;
  }
  async getGeoLocationFromIpAddress() {
    const url = new URL(this.internalConfig.geolocationApi);
    url.searchParams.set("ipaddress", this.internalConfig.ipAddress ?? "");
    return this.getGeoLocationFromApi(url.toString(), "ipaddress" /* Ipaddress */);
  }
  async setGeoLocationWithZip(zip) {
    const url = new URL(this.internalConfig.contextApi);
    url.searchParams.set("zip5", zip);
    return this.getGeoLocationFromApi(url.toString(), "zip5" /* Zip5 */);
  }
  async getGeoLocationFromApi(url, source) {
    try {
      const fetchedLocation = await this.fetcher(url.toString());
      return {
        ...formatLocationData(fetchedLocation),
        ipAddress: this.internalConfig.ipAddress ?? "",
        source
      };
    } catch (err) {
      const location = formatLocationOnError(this.cookies);
      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }
      return {
        ...location,
        ipAddress: location.ipAddress || this.internalConfig.ipAddress || "",
        source: "default" /* Default */
      };
    }
  }
  setLocationCookies({
    source: _source,
    ...locationWithoutSource
  }) {
    setCookies(this.cookies, locationWithoutSource, {
      path: "/",
      "Max-Age": TwoWeeks
    });
  }
  async updateAccountDeliveryInfo({
    CVIdToken,
    zip
  }) {
    try {
      const userInfo = jwtDecode(CVIdToken);
      if (userInfo.is_impersonating === "False")
        await this.fetcherWithAuth(
          `${this.internalConfig.contactsApi}/${userInfo.user_id}/addresses/delivery-zip5`,
          {
            body: JSON.stringify({
              contactId: userInfo.user_id,
              actorId: userInfo.impersonating_user,
              zip5: zip
            }),
            headers: {
              "Content-Type": "application/json"
            },
            method: "POST"
          }
        );
    } catch (err) {
      if (this.internalConfig.onError) {
        this.internalConfig.onError(toError(err));
      }
    }
  }
};

// src/api/http/axios.ts
function wrap(axios) {
  return (url, options) => {
    const requestConfig = {
      ...options,
      data: options?.body
    };
    return axios(url, requestConfig).then((res) => res.data);
  };
}

// src/api/http/got.ts
function wrap2(got) {
  return (url, options) => {
    const requestConfig = {
      ...options,
      method: options?.method
    };
    return got(url, requestConfig).json();
  };
}

// src/api/lib/generalizedCookieClient.ts
function generateCookieMap(cookieString) {
  return new Map(
    cookieString.split(/; ?/).map((cookie) => {
      const [key, value] = cookie.trim().split("=");
      return [key, decodeURI(value)];
    })
  );
}
var ServerCookies = class {
  constructor(request) {
    this.request = request;
  }
  getCookie() {
    const cookie = this.request.headers.cookie;
    if (!cookie) {
      throw new Error("Cookie header not found");
    }
    return cookie;
  }
};
var ExpressCookieAdapter = class extends ServerCookies {
  constructor(request, httpResponse) {
    super(request);
    this.httpResponse = httpResponse;
  }
  get cookie() {
    return this.getCookie();
  }
  set cookie(cookie) {
    this.httpResponse.append("Set-Cookie", cookie);
  }
};
var NodeCookieAdapter = class extends ServerCookies {
  constructor(request, httpResponse) {
    super(request);
    this.httpResponse = httpResponse;
  }
  get cookie() {
    return this.getCookie();
  }
  set cookie(cookie) {
    this.httpResponse.setHeader("Set-Cookie", cookie);
  }
};
var ClientCookieAdapter = class {
  constructor(document) {
    this.document = document;
  }
  get cookie() {
    return this.document.cookie;
  }
  set cookie(cookie) {
    this.document.cookie = cookie;
  }
};
var GeneralizedCookieClient = class {
  constructor(client) {
    this.client = client;
  }
  get(name) {
    const cookie = this.client.cookie.split(/; ?/gim).find((pairing) => pairing.startsWith(name)) ?? "";
    const [, value] = cookie.split("=");
    return value ? decodeURI(value) : void 0;
  }
  getAll() {
    return generateCookieMap(this.client.cookie);
  }
  /**
   * The cookies are stored in one line string like:
   * "CVCurrentCity='Mesa'; CVCurrentState='AZ'; CVCurrentZip='85201'; MerchHasLoggedIn='true'; "
   */
  set(name, value, options = {}) {
    const cookieItems = [`${name}=${decodeURI(value)}`];
    Object.entries(options).forEach(
      ([optionName, optionVal]) => cookieItems.push(`${capitalize(optionName)}=${optionVal}`)
    );
    this.client.cookie = cookieItems.join("; ").trim();
  }
};

// src/banner/index.tsx
import React2 from "react";

// src/banner/icons/ErrorFilled.tsx
import React from "react";
function ErrorFilled() {
  return /* @__PURE__ */ React.createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "24",
      height: "24",
      viewBox: "0 0 24 24",
      fill: "none"
    },
    /* @__PURE__ */ React.createElement(
      "path",
      {
        fillRule: "evenodd",
        clipRule: "evenodd",
        d: "M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12ZM12 6C12.5523 6 13 6.44772 13 7V13C13 13.5523 12.5523 14 12 14C11.4477 14 11 13.5523 11 13V7C11 6.44772 11.4477 6 12 6ZM12 18C12.5523 18 13 17.5523 13 17C13 16.4477 12.5523 16 12 16C11.4477 16 11 16.4477 11 17C11 17.5523 11.4477 18 12 18Z",
        fill: "#EB9F23"
      }
    )
  );
}

// src/banner/index.tsx
function DeliveryBanner(props) {
  const { zip, openCallback } = props;
  return /* @__PURE__ */ React2.createElement(
    "div",
    {
      "data-package": "geolocation",
      "data-qa": "delivery-banner",
      "data-testid": "delivery-banner"
    },
    /* @__PURE__ */ React2.createElement("div", { className: "align-center flex max-w-full justify-center bg-grey-1 py-[14px] text-base md:py-[18px]" }, /* @__PURE__ */ React2.createElement(ErrorFilled, null), /* @__PURE__ */ React2.createElement("div", { className: "px-8 font-sans text-blue-6 tl-header-xs md:tl-header-s" }, "Delivery unavailable for ", zip), /* @__PURE__ */ React2.createElement(
      "div",
      {
        className: "cursor-pointer font-sans text-blue-2 tl-header-xs hover:text-blue-3 md:tl-header-s",
        tabIndex: 0,
        onClick: () => {
          openCallback(true);
        },
        onKeyUp: (e) => e.key === "Enter" && openCallback(true),
        "data-analytics-id": "Delivery Banner - Change Location"
      },
      "Change Location"
    ))
  );
}

// src/modal/Modal.tsx
import React18 from "react";

// src/modal/components/DeliverableContent.tsx
import React15 from "react";

// src/modal/components/AcquisitionOptions/AcquisitionOptions.tsx
import React4 from "react";

// src/modal/context/LocationContext.tsx
import React3, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";

// src/modal/lib/hasValidLocation.ts
function hasValidLocation({ city, state, zip }) {
  const foundLocation = [city, state, zip].filter(Boolean);
  return Boolean(foundLocation.length === 3);
}

// src/modal/lib/maybeIsExcludedLocation.ts
function maybeIsExcludedLocation({ isMeetupAllowed, isDeliveryAllowed, pickUpInfoToDisplay }) {
  return Boolean(!isDeliveryAllowed && !isMeetupAllowed && !pickUpInfoToDisplay);
}

// src/modal/context/LocationContext.tsx
var defaultErrorState = {
  deliveryError: false,
  locationError: false
};
var defaultState = {
  city: "",
  state: "",
  zip: ""
};
var defaultContext = {
  ...defaultState,
  errorState: defaultErrorState,
  isExcludedLocation: false,
  onCloseHandler: () => {
  },
  setGeolocation: (_zipcode) => new Promise(
    (resolve) => resolve({
      city: "",
      source: void 0,
      state: "",
      zip: ""
    })
  )
};
var LocationContext = createContext(defaultContext);
var useLocationContext = () => useContext(LocationContext);
function LocationContextProvider({
  children,
  fetchDelivery,
  geoApi,
  isOpen,
  onCloseHandler,
  onUserLocationChange
}) {
  const [location, setLocation] = useState(defaultState);
  const [errorState, setErrorState] = useState(defaultErrorState);
  useEffect(() => {
    setErrorState(defaultErrorState);
  }, [isOpen]);
  const setGeolocation = useCallback(
    async (zipcode) => {
      const locationToSave = {
        city: "",
        state: "",
        zip: ""
      };
      const errors = {
        locationError: false,
        deliveryError: false
      };
      try {
        const res = await geoApi.setGeoLocation(zipcode);
        if (res.source === "default" /* Default */) {
          errors.locationError = true;
        } else {
          locationToSave.city = res.city;
          locationToSave.state = res.state;
          locationToSave.zip = res.zip;
        }
      } catch (err) {
        errors.locationError = true;
      }
      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isMeetupAllowed, isDeliveryAllowed } = await fetchDelivery(zipcode);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isMeetupAllowed = isMeetupAllowed;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }
      setErrorState(errors);
      if (!hasValidLocation(locationToSave)) {
        throw new Error("Location is invalid");
      }
      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }
      setLocation(locationToSave);
      return locationToSave;
    },
    [geoApi, fetchDelivery, onUserLocationChange]
  );
  useEffect(() => {
    (async () => {
      const locationToSave = {
        city: "",
        state: "",
        zip: ""
      };
      const errors = {
        locationError: false,
        deliveryError: false
      };
      try {
        const res = await geoApi.getGeoLocation();
        locationToSave.city = res.city;
        locationToSave.state = res.state;
        locationToSave.zip = res.zip;
      } catch (err) {
        errors.locationError = true;
      }
      try {
        if (!errors.locationError) {
          const { pickUpInfoToDisplay, isMeetupAllowed, isDeliveryAllowed } = await fetchDelivery(locationToSave.zip);
          locationToSave.pickUpInfoToDisplay = pickUpInfoToDisplay;
          locationToSave.isMeetupAllowed = isMeetupAllowed;
          locationToSave.isDeliveryAllowed = isDeliveryAllowed;
        }
      } catch (err) {
        errors.deliveryError = true;
      }
      if (onUserLocationChange) {
        onUserLocationChange({
          location: locationToSave,
          errorState: errors
        });
      }
      setErrorState(errors);
      setLocation(locationToSave);
    })();
  }, [fetchDelivery, geoApi, onUserLocationChange]);
  const isExcludedLocation = maybeIsExcludedLocation(location);
  const value = useMemo(
    () => ({
      ...location,
      errorState,
      isExcludedLocation,
      onCloseHandler,
      setGeolocation
    }),
    [errorState, isExcludedLocation, location, onCloseHandler, setGeolocation]
  );
  return /* @__PURE__ */ React3.createElement(LocationContext.Provider, { value }, children);
}
LocationContextProvider.displayName = "LocationContextProvider";

// src/modal/components/AcquisitionOptions/AcquisitionOptions.tsx
function AcquisitionOptions({ children }) {
  const { errorState, zip, isExcludedLocation } = useLocationContext();
  if (!zip && errorState.locationError || isExcludedLocation)
    return null;
  return /* @__PURE__ */ React4.createElement(
    "div",
    {
      "data-qa": "acquisition-options",
      "data-testid": "acquisition-options",
      className: "flex w-full justify-evenly s:flex-col s:max-m:pb-48 m:flex-row m:py-32"
    },
    children
  );
}

// src/modal/components/AcquisitionOptions/DeliveryOption.tsx
import React6 from "react";

// src/modal/components/AcquisitionOptions/AcquisitionOption.tsx
import React5 from "react";
function AcquisitionOption({
  title,
  text,
  conditionalClasses
}) {
  return /* @__PURE__ */ React5.createElement(
    "div",
    {
      "data-qa": `${title.toLowerCase().split(" ").join("-")}-option`,
      "data-testid": `${title.toLowerCase().split(" ").join("-")}-option`,
      className: `text-center before:content-[''] s:w-full s:max-m:relative s:max-m:pt-32 s:max-m:before:absolute s:max-m:before:left-0 s:max-m:before:right-0 s:max-m:before:top-0 s:max-m:before:m-auto s:max-m:before:h-[1px] s:max-m:before:w-[100px] s:max-m:before:bg-grey-2 s:max-m:first:pb-32 s:max-s:whitespace-nowrap m:w-1/2 m:px-20 ${conditionalClasses}`
    },
    /* @__PURE__ */ React5.createElement("p", { className: "m-0 uppercase text-blue-6 tl-header-m" }, title),
    /* @__PURE__ */ React5.createElement("p", { className: "m-0 mt-8 text-grey-5 tl-body-s" }, text)
  );
}

// src/modal/components/AcquisitionOptions/DeliveryOption.tsx
function DeliveryOption() {
  const { isDeliveryAllowed = true } = useLocationContext();
  return isDeliveryAllowed ? /* @__PURE__ */ React6.createElement(
    AcquisitionOption,
    {
      title: "delivery",
      text: "Yay! Delivery is available in your location."
    }
  ) : null;
}

// src/modal/components/AcquisitionOptions/PickupOption.tsx
import React7 from "react";
function PickUpOption() {
  const {
    pickUpInfoToDisplay,
    isDeliveryAllowed = true,
    isMeetupAllowed = false
  } = useLocationContext();
  const { pickupLocation, milesToPickupLocation } = pickUpInfoToDisplay ?? {};
  const pickupInfo = ` ${pickupLocation?.city}, ${pickupLocation?.state} (${milesToPickupLocation} miles away)`;
  const dividerClasses = isDeliveryAllowed || isMeetupAllowed ? "m:first:border-0 m:first:border-r m:first:border-grey-2 m:first:border-solid" : "";
  return /* @__PURE__ */ React7.createElement(
    AcquisitionOption,
    {
      conditionalClasses: dividerClasses,
      title: "pick up",
      text: `The nearest pickup location is ${pickupInfo}.`
    }
  );
}

// src/modal/components/Cta.tsx
import cx from "classnames";
import React8 from "react";
function ModalCta({ ctaText }) {
  const { isDeliveryAllowed = true } = useLocationContext();
  return /* @__PURE__ */ React8.createElement(
    "div",
    {
      "data-qa": "delivery-text",
      "data-testid": "delivery-text",
      className: cx("pt-32 text-center text-blue-6 tl-header-m s:max-m:px-24", {
        "bg-grey-1 pb-0": isDeliveryAllowed,
        "pb-32": !isDeliveryAllowed
      })
    },
    ctaText
  );
}

// src/modal/components/LocationDisplay/CityStateInfo.tsx
import cx2 from "classnames";
import React10 from "react";

// src/modal/components/LocationDisplay/Location.tsx
import React9 from "react";
function Location() {
  const { city, state } = useLocationContext();
  return /* @__PURE__ */ React9.createElement(
    "p",
    {
      "data-qa": "location",
      "data-testid": "location",
      className: "m-0 mt-[6px] flex justify-center text-center align-middle text-blue-6 tl-header-s"
    },
    /* @__PURE__ */ React9.createElement(
      "svg",
      {
        className: "mr-8",
        width: "14",
        height: "19",
        viewBox: "0 0 14 19",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg"
      },
      /* @__PURE__ */ React9.createElement(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M6.99943 0.946777C10.5081 0.946777 13.3524 3.79109 13.3524 7.29972C13.3524 12 7.7406 18.5127 7.7406 18.5127C7.57191 18.762 7.29943 18.9216 6.99943 18.9468C6.70731 18.9211 6.43893 18.7754 6.25825 18.5444C6.25825 18.5444 0.646484 12 0.646484 7.29972C0.686527 3.80781 3.50751 0.98682 6.99943 0.946777ZM9.37115 7.16188C9.38277 5.83866 8.32263 4.75494 6.99947 4.73745C5.66445 4.73157 4.57583 5.80599 4.56418 7.14098C4.5643 8.46425 5.634 9.53852 6.95726 9.54428C8.28052 9.55003 9.35953 8.4851 9.37115 7.16188Z",
          fill: "#122946"
        }
      )
    ),
    /* @__PURE__ */ React9.createElement("span", null, city, ", ", state)
  );
}

// src/modal/components/LocationDisplay/CityStateInfo.tsx
function CityStateInfo() {
  const {
    errorState,
    city,
    isDeliveryAllowed = true,
    isExcludedLocation
  } = useLocationContext();
  if (!city && errorState.locationError || isExcludedLocation)
    return null;
  return /* @__PURE__ */ React10.createElement(
    "div",
    {
      "data-qa": "city-state-info",
      "data-testid": "city-state-info",
      className: cx2(
        "w-11/12 text-center s:max-m:pb-32 m:border-0 m:border-b m:border-solid m:border-grey-2 m:p-24 m:pb-[26px]",
        {
          "m:pb-4": !isDeliveryAllowed,
          "m:border-b-0": !isDeliveryAllowed,
          "m:border-t": !isDeliveryAllowed
        }
      )
    },
    /* @__PURE__ */ React10.createElement("p", { className: "m-0 text-grey-5 tl-body-s" }, "Options for getting your vehicle in:"),
    /* @__PURE__ */ React10.createElement(Location, null)
  );
}

// src/modal/components/LocationDisplay/LocationDisplay.tsx
import cx3 from "classnames";
import React11 from "react";
function LocationDisplay({ children }) {
  const { isDeliveryAllowed = true } = useLocationContext();
  return /* @__PURE__ */ React11.createElement(
    "div",
    {
      "data-qa": "availability-info",
      "data-testid": "availability-info",
      className: cx3(
        "flex flex-col items-center s:max-m:px-24 m:justify-center",
        {
          "bg-white": isDeliveryAllowed,
          "s:max-m:h-screen": isDeliveryAllowed,
          "s:max-m:pt-32": isDeliveryAllowed
        }
      )
    },
    children
  );
}

// src/modal/components/LocationInputForm/FormWrapper.tsx
import cx4 from "classnames";
import React14 from "react";

// src/modal/components/LocationInputForm/Form.tsx
import React12, { useState as useState2, useEffect as useEffect2 } from "react";

// src/modal/components/LocationInputForm/Form.styles.ts
import { createComponent } from "@carvana/merch-components";
var FieldSet = createComponent("fieldset", {
  className: `
    relative
    mr-8
    w-[129px]
    rounded
    border
    border-solid
    border-grey-2
    bg-white
    p-0
    outline-none
    focus-within:border-blue-6
  `
});
var Input = createComponent(
  "input",
  {
    className: `
    peer
    absolute
    left-0
    box-border
    block
    w-full
    min-w-0
    overflow-visible
    border-none
    bg-transparent
    px-0
    pb-[7px]
    pt-[22px]
    indent-16
    text-base
    placeholder-transparent
    outline-none
    tl-body-m
    placeholder:text-grey-3
    focus:!text-blue-6
  `
  }
);
var Label = createComponent("label", {
  className: `
    absolute
    left-16
    top-[2px]
    z-0
    mb-[8px]
    block
    origin-top-left
    translate-x-0
    translate-y-[11px]
    scale-[0.63]
    transform
    p-0
    text-[13px]
    font-bold
    uppercase
    leading-[13px]
    text-blue-2
    transition
    delay-[0]
    duration-200
    ease-out
    peer-placeholder-shown:translate-x-0
    peer-placeholder-shown:translate-y-[19px]
    peer-placeholder-shown:scale-100
    peer-placeholder-shown:text-grey-2
    peer-focus:translate-x-0
    peer-focus:translate-y-[11px]
    peer-focus:scale-[0.63]
    peer-focus:text-blue-2
  `
});
var Button = createComponent("button", {
  className: `
    h-56
    w-[100px]
    rounded
    border-0
    bg-blue-2
    font-bold
    uppercase
    leading-none
    text-white
    hover:bg-blue-3
    disabled:text-grey-5
  `
});

// src/modal/components/LocationInputForm/getSubmissionCallback.ts
function getSubmissionCallback(fn, value) {
  return ({ zip: currentZip }) => {
    if (!currentZip)
      fn(value);
  };
}

// src/modal/components/LocationInputForm/handleSelectOnFocus.ts
function handleSelectOnFocus(e) {
  e.target.select();
}

// src/modal/components/LocationInputForm/handleSubmit.ts
function handleSubmit(fn, value, onSubmitCallback, closeHandler) {
  return (e) => {
    e.preventDefault();
    fn(value).then(onSubmitCallback).then(closeHandler).catch(() => {
    });
  };
}

// src/modal/components/LocationInputForm/Form.tsx
var LABEL = "zip code";
function Form() {
  const { zip, setGeolocation, onCloseHandler } = useLocationContext();
  const [inputValue, setInputValue] = useState2(() => zip);
  useEffect2(() => {
    setInputValue(zip);
  }, [zip]);
  return /* @__PURE__ */ React12.createElement(
    "form",
    {
      "data-testid": "zipcode-form",
      onSubmit: handleSubmit(
        setGeolocation,
        inputValue,
        getSubmissionCallback(setInputValue, zip),
        onCloseHandler
      ),
      className: "mt-24 flex align-top"
    },
    /* @__PURE__ */ React12.createElement(FieldSet, null, /* @__PURE__ */ React12.createElement(
      Input,
      {
        "data-qa": "zipcode-form-input",
        "data-testid": "zipcode-form-input",
        id: LABEL,
        maxLength: 5,
        name: "zipcode",
        onBlur: () => {
          if (inputValue === zip || !inputValue) {
            setInputValue(zip);
          }
        },
        onFocus: handleSelectOnFocus,
        onChange: (e) => {
          setInputValue(e.currentTarget.value);
        },
        value: inputValue,
        type: "text",
        title: "Please enter a valid zip code.",
        placeholder: "Zip Code",
        pattern: "[0-9]{5}"
      }
    ), /* @__PURE__ */ React12.createElement(
      Label,
      {
        "data-qa": "zipcode-form-label",
        "data-testid": "zipcode-form-label",
        htmlFor: LABEL
      },
      LABEL
    )),
    /* @__PURE__ */ React12.createElement(
      Button,
      {
        "data-analytics-id": "Geolocation - Changed Zip",
        type: "submit",
        "data-testid": "zipcode-form-submit"
      },
      "UPDATE"
    )
  );
}

// src/modal/components/LocationDisplay/LocationWrapper.tsx
import React13 from "react";
function LocationWrapper({ children }) {
  const { isExcludedLocation } = useLocationContext();
  return isExcludedLocation ? /* @__PURE__ */ React13.createElement(
    "div",
    {
      "data-qa": "location-wrapper",
      "data-testid": "location-wrapper",
      className: "pt-24"
    },
    children
  ) : null;
}

// src/modal/components/LocationInputForm/FormWrapper.tsx
function LocationInputForm({ formHeading }) {
  const { isDeliveryAllowed = true } = useLocationContext();
  return /* @__PURE__ */ React14.createElement(
    "div",
    {
      "data-qa": "form-wrapper",
      "data-testid": "form-wrapper",
      className: cx4(
        "flex flex-col items-center border-0 border-b border-solid border-grey-2 bg-grey-1 px-24 pb-32",
        { "pb-48 pt-32 s:max-m:h-full": !isDeliveryAllowed }
      )
    },
    /* @__PURE__ */ React14.createElement(
      "p",
      {
        "data-qa": "form-heading",
        "data-testid": "form-heading",
        className: cx4(
          "m-0 pt-8 text-center tl-body-m",
          { "w-[257px]": !isDeliveryAllowed },
          formHeading.type === "error" ? "text-red-2" : "text-grey-5"
        )
      },
      formHeading.text
    ),
    /* @__PURE__ */ React14.createElement(Form, null),
    /* @__PURE__ */ React14.createElement(LocationWrapper, null, /* @__PURE__ */ React14.createElement(Location, null))
  );
}

// src/modal/lib/getFormHeading.ts
var headingMap = /* @__PURE__ */ new Map([
  [
    "hasErrored",
    {
      text: "Unable to set your location, please try again.",
      type: "error"
    }
  ],
  [
    "isExcludedLocation",
    {
      text: "Please update your ZIP code to purchase from a different location",
      type: "error"
    }
  ],
  [
    "isDeliveryAllowed",
    {
      text: "We'll show you the most accurate shipping cost and delivery estimates based on your location.",
      type: "message"
    }
  ]
]);
var getFirstTruthyOption = (options) => {
  const [firstTruthyOption = ""] = Object.entries(options).find(([, val]) => val) ?? [];
  return firstTruthyOption;
};
function getFormHeading({
  hasErrored,
  isExcludedLocation,
  isDeliveryAllowed
}) {
  const headingOption = getFirstTruthyOption({
    hasErrored,
    isExcludedLocation,
    isDeliveryAllowed
  });
  const formHeading = headingMap.get(headingOption);
  return formHeading ?? {
    text: "Or you can update your ZIP code to purchase from a different location.",
    type: "message"
  };
}

// src/modal/lib/maybeErrored.ts
function getHasErrored({
  locationError,
  deliveryError
}) {
  return Boolean(locationError || deliveryError);
}

// src/modal/components/DeliverableContent.tsx
function DeliverableContent() {
  const {
    isDeliveryAllowed = true,
    errorState,
    isExcludedLocation
  } = useLocationContext();
  const hasErrored = getHasErrored(errorState);
  const formHeading = getFormHeading({
    hasErrored,
    isExcludedLocation,
    isDeliveryAllowed
  });
  return isDeliveryAllowed ? /* @__PURE__ */ React15.createElement(React15.Fragment, null, /* @__PURE__ */ React15.createElement(ModalCta, { ctaText: "Enter Your ZIP Code" }), /* @__PURE__ */ React15.createElement(LocationInputForm, { formHeading }), /* @__PURE__ */ React15.createElement(LocationDisplay, null, /* @__PURE__ */ React15.createElement(CityStateInfo, null), /* @__PURE__ */ React15.createElement(AcquisitionOptions, null, /* @__PURE__ */ React15.createElement(PickUpOption, null), /* @__PURE__ */ React15.createElement(DeliveryOption, null)))) : null;
}

// src/modal/components/UndeliverableContent.tsx
import React17 from "react";

// src/modal/components/AcquisitionOptions/MeetupOption.tsx
import React16 from "react";
function MeetUpOption() {
  const { isMeetupAllowed = false } = useLocationContext();
  return isMeetupAllowed ? /* @__PURE__ */ React16.createElement(
    AcquisitionOption,
    {
      title: "meet up",
      text: "Meet us somewhere in the middle."
    }
  ) : null;
}

// src/modal/components/UndeliverableContent.tsx
function UndeliverableContent() {
  const { isDeliveryAllowed = true, errorState, isExcludedLocation } = useLocationContext();
  const hasErrored = getHasErrored(errorState);
  const formHeading = getFormHeading({ hasErrored, isExcludedLocation, isDeliveryAllowed });
  const ctaText = isExcludedLocation ? "Delivery Is Unavailable In Your Area" : "Delivery Is Unavailable, But You Still Have Options";
  return !isDeliveryAllowed ? /* @__PURE__ */ React17.createElement(React17.Fragment, null, /* @__PURE__ */ React17.createElement(ModalCta, { ctaText }), /* @__PURE__ */ React17.createElement(LocationDisplay, null, /* @__PURE__ */ React17.createElement(CityStateInfo, null), /* @__PURE__ */ React17.createElement(AcquisitionOptions, null, /* @__PURE__ */ React17.createElement(PickUpOption, null), /* @__PURE__ */ React17.createElement(MeetUpOption, null))), /* @__PURE__ */ React17.createElement(LocationInputForm, { formHeading })) : null;
}

// src/modal/Modal.tsx
function ModalContents() {
  return /* @__PURE__ */ React18.createElement("div", { "data-package": "geolocation", "data-testid": "geolocation-modal" }, /* @__PURE__ */ React18.createElement("div", { className: "bg-white" }, /* @__PURE__ */ React18.createElement(DeliverableContent, null), /* @__PURE__ */ React18.createElement(UndeliverableContent, null)));
}
function Modal({ children, config, isOpen }) {
  return /* @__PURE__ */ React18.createElement(LocationContextProvider, { isOpen, ...config }, children ? children({ content: /* @__PURE__ */ React18.createElement(ModalContents, null) }) : /* @__PURE__ */ React18.createElement(ModalContents, null));
}
export {
  ClientCookieAdapter,
  CookieNames,
  DefaultLocation,
  DeliveryBanner,
  ExpressCookieAdapter,
  GeneralizedCookieClient,
  GeoLocationApi,
  Modal as GeoModal,
  LocationSource,
  NodeCookieAdapter,
  generateCookieMap,
  wrap as wrapAxios,
  wrap2 as wrapGot
};
//# sourceMappingURL=index.js.map
